<template>
    <div v-if="isOpenSideBar && favProjects.length >= 1" :class="[$style.favProjects]">
        <div
            id="gp_autotest_sidebar_favorite_projects_title"
            :class="[$style.title, activeTitle ? $style.active : '']"
            @click="hideShowFavProjects"
        >
            <div :class="$style.title_wrapper">
                <svg-sprite name="star" type="bold" :size="20" :class="$style.title_wrapper_star" />
                <div :class="$style.title_text">
                    {{ locale("sidebar_favorite_projects_title") }}
                </div>
            </div>

            <svg-sprite
                name="arrow-down"
                :size="20"
                :class="[$style.title_arrow, openFavProjects ? $style.rotated : '']"
            />
        </div>
        <div v-show="openFavProjects" :class="[$style.fav_list, isEndScrolled ? '' : $style.haze]">
            <div
                ref="projectsScroll"
                v-scroll.self="handleScrollProjects"
                :class="$style.fav_scroll"
                :style="{ height: `${favHeight}px` }"
            >
                <draggable
                    v-model="localFavProjects"
                    :item-key="(project) => project.id"
                    v-bind="{
                        animation: 200,
                        forceFallback: true,
                        fallbackClass: $style.drag_fallback,
                        fallbackTolerance: 5,
                        easing: 'cubic-bezier(1, 0, 0, 1)',
                    }"
                    :class="{ [$style.dragging_active]: isDragging }"
                    @start="handleDragStart"
                    @end="handleDragEnd"
                    :move="handleDragMove"
                >
                    <template #item="{ element: project }">
                        <div
                            :data-id="project.id"
                            :data-gantt_id="project.gantt_id"
                            :data-type="project.type"
                            :class="[
                                $style.project_item,
                                +activeItem === project.id ? $style.active : '',
                                pressedItemId === project.id ? $style.pressed : '',
                                draggingId === project.id ? $style.is_ghost : '',
                                'gp_autotest_sidebar_favorite_projects_item',
                            ]"
                            @mousedown="handleMouseDown(project.id)"
                            @mouseup="handleMouseUp"
                            @mouseleave="handleMouseUp"
                            @click="!isDragging && projectClick(project)"
                        >
                            <div :class="[$style.drag_handle, 'drag_handle']">
                                <svg-sprite :name="'dragdrop'" :size="20" />
                            </div>
                            <svg-sprite :class="$style.project_item_icon" :name="getProjectIcon(project)" :size="20" />
                            <div :title="project.name" :class="$style.project_item_text">
                                {{ project.name }}
                            </div>
                            <div :class="$style.project_item_star" @click.stop="handleUnfavorite(project)">
                                <svg-sprite :name="'unfavorite'" :size="20" />
                            </div>
                        </div>
                    </template>
                </draggable>
            </div>
        </div>
    </div>
</template>

<script>
import globalStore from "$$globalStore";
import { api, internalApi } from "$$store/api";
import draggable from "vuedraggable";
import getCurrentUserId from "../../../components/rights/helpers/getCurrentUserId";
import SvgSprite from "../../common/VueComponents/VueIcon/svgSprite.vue";

export default {
    name: "FavoriteProjects",
    components: { SvgSprite, draggable },
    props: {
        isOpenSideBar: { type: Boolean, required: true },
        favProjects: { type: Array, required: true },
        openFavProjects: { type: Boolean, required: true },
        favHeight: { type: Number, required: true },
        activeItem: { type: String, required: false, default: "" },
    },
    emits: ["hideShowFavProjects", "projectClick", "orderChanged"],
    data() {
        return {
            locale: __,
            isEndScrolled: false,
            localFavProjects: [],
            previousOrder: [],
            isDragging: false,
            favoriteOrderMap: null,
            pressedItemId: null,
            dragTargetIndex: null,
            draggingId: null,
        };
    },
    computed: {
        activeTitle() {
            const activeItem = this.favProjects.find((project) => +project.id === +this.activeItem);

            if (activeItem && !this.openFavProjects) {
                return true;
            }

            return false;
        },
    },
    watch: {
        openFavProjects(value) {
            if (value) setTimeout(this.handleScrollProjects);
        },
        favHeight() {
            setTimeout(this.handleScrollProjects);
        },
        favProjects: {
            handler(newProjects) {
                const sortedProjects = this.applyFavoriteOrder(newProjects);
                this.localFavProjects = [...sortedProjects];

                if (this.favoriteOrderMap && Array.isArray(this._lastFavIds)) {
                    const currentIds = newProjects.map((project) => project.id);
                    this._lastFavIds
                        .filter((id) => !currentIds.includes(id))
                        .forEach((id) => this.favoriteOrderMap.delete(id));
                    this._lastFavIds = currentIds;
                } else {
                    this._lastFavIds = newProjects.map((project) => project.id);
                }

                setTimeout(this.handleScrollProjects);
            },
            immediate: true,
        },
    },
    async mounted() {
        await this.loadFavoriteOrder();
    },
    methods: {
        hideShowFavProjects() {
            this.$emit("hideShowFavProjects");
        },
        projectClick(project) {
            this.$emit("projectClick", project);
        },
        handleUnfavorite(project) {
            if (this.isDragging) {
                return;
            }

            const id = project.id;
            const isMultiview = !!(project.ganttIDs || (project.config && project.config.multiview));

            if (this.favoriteOrderMap) {
                this.favoriteOrderMap.delete(id);
            }

            globalStore.dispatch("headerView/toggleFavorite", { id, isMultiview });
        },
        getProjectIcon(project) {
            let icon = "";

            if (project.is_jira) {
                icon = "jira";
            } else {
                icon = project.ganttIDs ? "portfolio" : "";
            }

            return icon;
        },
        handleScrollProjects() {
            const el = this.$refs.projectsScroll;

            if (!el) return false;

            const isScrollable = el.scrollHeight > el.offsetHeight + 1;
            const isAtBottom = el.scrollTop + el.offsetHeight >= el.scrollHeight - 1;

            this.isEndScrolled = !isScrollable || isAtBottom;
        },
        async loadFavoriteOrder() {
            if (this.favoriteOrderMap) return;
            try {
                let favoriteOrderRaw = window.user?.settings?.favorite_order;

                if (!favoriteOrderRaw) {
                    const { data: userSettings } = await api.get("/userSettings");
                    favoriteOrderRaw = userSettings?.favorite_order;
                }
                if (favoriteOrderRaw) {
                    const favoriteOrder =
                        typeof favoriteOrderRaw === "string" ? JSON.parse(favoriteOrderRaw) : favoriteOrderRaw;
                    this.favoriteOrderMap = new Map();
                    favoriteOrder.forEach((item) => {
                        const key = item.project_id || item.multiview_id;
                        if (key) {
                            this.favoriteOrderMap.set(key, item.order);
                        }
                    });
                    const sortedProjects = this.applyFavoriteOrder(this.localFavProjects);
                    this.localFavProjects = [...sortedProjects];
                }
            } catch (error) {}
        },
        applyFavoriteOrder(projects) {
            if (!projects || projects.length === 0) {
                return projects;
            }

            const getTimestamp = (project) => {
                const date = project.favorite_created_at || project.created_at || project.last_update;
                if (date) {
                    if (typeof date === 'number') return date;
                    const ts = new Date(date).getTime();
                    if (!isNaN(ts)) return ts;
                }
                return +project.id || 0;
            };

            if (!this.favoriteOrderMap) {
                return [...projects].sort((a, b) => getTimestamp(b) - getTimestamp(a));
            }

            const projectsWithOrder = projects.map((project) => ({
                ...project,
                favorite_order: this.favoriteOrderMap.get(project.id) ?? -1,
            }));

            const noOrder = projectsWithOrder.filter((p) => p.favorite_order === -1);
            const withOrder = projectsWithOrder.filter((p) => p.favorite_order !== -1);

            noOrder.sort((a, b) => getTimestamp(b) - getTimestamp(a));
            withOrder.sort((a, b) => a.favorite_order - b.favorite_order);

            return [...noOrder, ...withOrder];
        },
        handleMouseDown(projectId) {
            this.pressedItemId = projectId;
        },
        handleMouseUp() {
            this.pressedItemId = null;
        },
        handleDragStart(event) {
            this.isDragging = true;
            this.draggingId = +event.item.dataset.id;
            this.previousOrder = [...this.localFavProjects];
            this.dragTargetIndex = null;
            document.body.classList.add("favorite-projects-dragging");
        },
        handleDragMove(event) {
            const allItems = this.$el.querySelectorAll(`.${this.$style.project_item}`);
            allItems.forEach((item) => {
                item.classList.remove(this.$style.drag_over, this.$style.drag_over_after);
            });
            if (event.related && event.related !== event.dragged) {
                let relatedItem = event.related;
                if (!relatedItem.classList.contains(this.$style.project_item)) {
                    relatedItem = relatedItem.closest(`.${this.$style.project_item}`);
                }

                if (relatedItem && relatedItem !== event.dragged) {
                    const relatedIndex = Array.from(allItems).indexOf(relatedItem);
                    if (relatedIndex !== -1) {
                        this.dragTargetIndex = relatedIndex + (event.willInsertAfter ? 1 : 0);
                    }

                    if (event.willInsertAfter) {
                        relatedItem.classList.add(this.$style.drag_over_after);
                    } else {
                        relatedItem.classList.add(this.$style.drag_over);
                    }
                }
            }

            const scrollContainer = this.$refs.projectsScroll;
            const pointerEvent = event.originalEvent || event;

            if (scrollContainer && pointerEvent && typeof pointerEvent.clientY === "number") {
                const rect = scrollContainer.getBoundingClientRect();
                const THRESHOLD = 40;
                const SCROLL_STEP = 12;

                if (pointerEvent.clientY > rect.bottom - THRESHOLD) {
                    scrollContainer.scrollTop += SCROLL_STEP;
                    this.handleScrollProjects();
                } else if (pointerEvent.clientY < rect.top + THRESHOLD) {
                    scrollContainer.scrollTop -= SCROLL_STEP;
                    this.handleScrollProjects();
                }
            }

            return false;
        },
        async handleDragEnd(event) {
            const movedId = +event.item?.dataset?.id;
            this.isDragging = false;
            this.draggingId = null;
            document.body.classList.remove("favorite-projects-dragging");
            this.$nextTick(() => {
                const allItems = this.$el.querySelectorAll(`.${this.$style.project_item}`);
                allItems.forEach((item) => {
                    item.classList.remove(this.$style.drag_over, this.$style.drag_over_after);
                });
            });
            const from = event.oldIndex;
            let to = this.dragTargetIndex;
            if (to === null || to === undefined) {
                to = from;
            }
            const adjustedTo = to > from ? to - 1 : to;

            if (adjustedTo !== from) {
                const updated = [...this.localFavProjects];
                const [moved] = updated.splice(from, 1);
                updated.splice(adjustedTo, 0, moved);
                this.localFavProjects = updated;
            }

            const orderChanged =
                JSON.stringify(this.previousOrder.map((p) => p.id)) !==
                JSON.stringify(this.localFavProjects.map((p) => p.id));

            if (!orderChanged) {
                this.$nextTick(() => {
                    this.scrollMovedItemIntoView(movedId);
                });
                return;
            }

            const newOrder = this.localFavProjects.map((project, index) => {
                const isMultiview = !!(project.ganttIDs || (project.config && project.config.multiview));

                return {
                    project_id: !isMultiview ? project.id : null,
                    multiview_id: isMultiview ? project.id : null,
                    order: index + 1,
                };
            });
            try {
                await internalApi.put(`/users/${getCurrentUserId()}/settings`, {
                    favOrder: newOrder,
                });
                this.favoriteOrderMap = new Map();
                newOrder.forEach((item) => {
                    const key = item.project_id || item.multiview_id;
                    if (key) {
                        this.favoriteOrderMap.set(key, item.order);
                    }
                });

                this.localFavProjects = this.localFavProjects.map((project, index) => ({
                    ...project,
                    favorite_order: index + 1,
                }));

                this.$emit("orderChanged", this.localFavProjects);
            } catch (error) {
                console.error("[FavoriteProjects] Failed to update order:", error);
                this.localFavProjects = [...this.previousOrder];
                this.$emit("orderChanged", this.localFavProjects);
            }

            this.$nextTick(() => {
                this.scrollMovedItemIntoView(movedId);
            });
        },
        scrollMovedItemIntoView(movedId) {
            if (!movedId) return;
            const container = this.$refs.projectsScroll;
            if (!container) return;

            const itemEl = container.querySelector(`[data-id="${movedId}"]`);
            if (!itemEl) return;

            itemEl.scrollIntoView({ block: "nearest", behavior: "smooth" });
            this.handleScrollProjects();
        },
    },
};
</script>

<style module src="../style.less" lang="less"></style>
